import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/services/firebase/firebase";

// Share only active read requests; discard successes and failures immediately.
const pendingCalendarReads = new Map<string, Promise<any>>();
const readCalendar = <T>(name: string, data?: Record<string, string>): Promise<T> => {
  const key = JSON.stringify([auth.currentUser?.uid || null, name, data]);
  const pending = pendingCalendarReads.get(key);
  if (pending) return pending;
  const request = httpsCallable(functions, name)(data)
    .then((result) => result.data as T)
    .finally(() => { pendingCalendarReads.delete(key); });
  pendingCalendarReads.set(key, request);
  return request;
};

export interface GoogleCalendarStatus {
  connected: boolean;
  availabilityEnabled: boolean;
  requiresReconnect?: boolean;
  email: string;
  timeZone?: string;
  schedulingTimeZone?: string;
}

export interface GoogleCalendarBusyRange {
  start: string;
  end: string;
}

export interface GoogleCalendarBusyTimesResult {
  busyRanges: GoogleCalendarBusyRange[];
  connected: boolean;
  availabilityEnabled: boolean;
  requiresReconnect: boolean;
  timeZone?: string;
  calendarTimeZone?: string;
}

export const getGoogleCalendarStatus =
  async (): Promise<GoogleCalendarStatus> => {
    return readCalendar<GoogleCalendarStatus>("getGoogleCalendarStatus");
  };

export const connectGoogleCalendar = async (): Promise<void> => {
  const callable = httpsCallable(functions, "getGoogleCalendarAuthUrl");
  const result = await callable({
    returnUrl: window.location.href,
  });
  const { authUrl } = result.data as { authUrl?: string };

  if (!authUrl) {
    throw new Error("No Google Calendar authorization URL was returned");
  }

  const authWindow = window.open(
    authUrl,
    "google-calendar-oauth",
    "width=520,height=720",
  );

  if (!authWindow) {
    throw new Error("Google Calendar authorization window was blocked");
  }
};

export const disconnectGoogleCalendar = async (): Promise<void> => {
  const callable = httpsCallable(functions, "disconnectGoogleCalendar");
  await callable();
};

export const getGoogleCalendarBusyTimes = async ({
  lawyerUid,
  date,
}: {
  lawyerUid: string;
  date: string;
}): Promise<GoogleCalendarBusyTimesResult> => {
  const callable = httpsCallable(functions, "getGoogleCalendarBusyTimes");
  const result = await callable({ lawyerUid, date });
  const data = result.data as {
    connected?: boolean;
    availabilityEnabled?: boolean;
    requiresReconnect?: boolean;
    busy?: GoogleCalendarBusyRange[];
    timeZone?: string;
    calendarTimeZone?: string;
  };

  return {
    busyRanges: data.connected && Array.isArray(data.busy) ? data.busy : [],
    connected: data.connected === true,
    availabilityEnabled: data.availabilityEnabled === true,
    requiresReconnect: data.requiresReconnect === true,
    timeZone: data.connected ? data.timeZone : undefined,
    calendarTimeZone: data.connected ? data.calendarTimeZone : undefined,
  };
};

export const getGoogleCalendarBusyTimesRange = async ({
  lawyerUid,
  startDate,
  endDate,
}: {
  lawyerUid: string;
  startDate: string;
  endDate: string;
}): Promise<GoogleCalendarBusyRange[]> => {
  const data = await readCalendar<{ connected?: boolean; busy?: GoogleCalendarBusyRange[] }>(
    "getGoogleCalendarBusyTimesRange", { lawyerUid, startDate, endDate },
  );

  return data.connected && Array.isArray(data.busy) ? data.busy : [];
};
